import { useState, useEffect } from 'react';
import { useCookieConsent } from '@/lib/cookie-consent';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Shield, Palette, BarChart3 } from 'lucide-react';

const categories = [
  {
    key: 'necessary' as const,
    icon: Shield,
    titlePt: 'Cookies Necessários',
    titleEn: 'Necessary Cookies',
    descPt: 'Essenciais para o funcionamento do site. Incluem autenticação, segurança e preferências de sessão. Não podem ser desativados.',
    descEn: 'Essential for website functionality. Include authentication, security, and session preferences. Cannot be disabled.',
    locked: true,
  },
  {
    key: 'functional' as const,
    icon: Palette,
    titlePt: 'Cookies Funcionais',
    titleEn: 'Functional Cookies',
    descPt: 'Permitem guardar preferências como o tema (claro/escuro) e o idioma selecionado, melhorando a sua experiência de utilização.',
    descEn: 'Allow saving preferences like theme (light/dark) and selected language, improving your browsing experience.',
    locked: false,
  },
  {
    key: 'analytics' as const,
    icon: BarChart3,
    titlePt: 'Cookies Analíticos',
    titleEn: 'Analytics Cookies',
    descPt: 'Ajudam-nos a compreender como os visitantes interagem com o site, permitindo melhorar continuamente o serviço. Atualmente não ativos.',
    descEn: 'Help us understand how visitors interact with the site, allowing us to continuously improve. Currently not active.',
    locked: false,
  },
];

export function CookieSettings() {
  const { isSettingsOpen, setSettingsOpen, functional, analytics, savePreferences } = useCookieConsent();
  const [localFunctional, setLocalFunctional] = useState(functional);
  const [localAnalytics, setLocalAnalytics] = useState(analytics);

  useEffect(() => {
    if (isSettingsOpen) {
      setLocalFunctional(functional);
      setLocalAnalytics(analytics);
    }
  }, [isSettingsOpen, functional, analytics]);

  const handleSave = () => {
    savePreferences({ functional: localFunctional, analytics: localAnalytics });
  };

  return (
    <Dialog open={isSettingsOpen} onOpenChange={setSettingsOpen}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Definições de Cookies</DialogTitle>
          <DialogDescription>
            Personalize as suas preferências de cookies. Os cookies necessários são sempre ativos.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {categories.map((cat) => {
            const Icon = cat.icon;
            const isChecked = cat.key === 'necessary' ? true
              : cat.key === 'functional' ? localFunctional
              : localAnalytics;
            const onToggle = cat.key === 'functional'
              ? setLocalFunctional
              : cat.key === 'analytics'
              ? setLocalAnalytics
              : undefined;

            return (
              <div key={cat.key} className="flex items-start gap-4 p-4 rounded-lg border border-border bg-muted/30">
                <Icon className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <h4 className="text-sm font-medium text-foreground">{cat.titlePt}</h4>
                    <Switch
                      checked={isChecked}
                      onCheckedChange={onToggle}
                      disabled={cat.locked}
                      aria-label={cat.titlePt}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{cat.descPt}</p>
                </div>
              </div>
            );
          })}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setSettingsOpen(false)}>Cancelar</Button>
          <Button onClick={handleSave}>Guardar Preferências</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
